import { createContext, FC, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { getCCAVisitReportDetail } from '@/libs/fleet-api/services/visit-reports';
import MainInformation from '@/components/MainInformation';
import { Divider, Tabs } from 'antd';
import FleetTitle from '@/libs/antd/components/Title';
import CCAVisitReportVerificationOffice from './VisitReportVerificationPage.Office';
import CCAVisitReportVerificationSite from './VisitReportVerificationPage.Site';
import CCAVisitReportVerificationCall from './VisitReportVerificationPage.Call';
import CCAVisitReportApprovalForm from './VisitReportVerificationPage.Approval';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const useGetCCAVisitReportDetail = (prospectId: string) => {
	return useQuery({
		queryKey: ['getCCAVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getCCAVisitReportDetail(id);
		},
	});
};

type CCAVisitReportDetail = ReturnType<
	typeof useGetCCAVisitReportDetail
>['data'];

const context = createContext<CCAVisitReportDetail | null>(null);

const { Provider } = context;

export const useCCAVisitReportVerificationPage = () => {
	const { data } = useContext(context)!;
	return data;
};

const CCAVisitReportVerificationPage: FC<{ prospectId: string }> = ({
	prospectId,
}) => {
	const { data, isLoading, isFetching, isError } =
		useGetCCAVisitReportDetail(prospectId);

	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
	} = data?.data ?? {};

	const [activeTabKey, setActiveTabKey] = useState<string>('office');

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Provider value={data}>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				isLoading={isLoading}
			/>
			<Divider />
			<FleetTitle>Verifikasi Laporan Kunjungan</FleetTitle>
			<Tabs
				activeKey={activeTabKey}
				onChange={(key) => setActiveTabKey(key)}
				items={[
					{
						key: 'office',
						label: 'Laporan Kunjungan Kantor',
						disabled: true,
						children: (
							<CCAVisitReportVerificationOffice
								onNext={() => {
									setActiveTabKey('site');
								}}
							/>
						),
					},
					{
						key: 'site',
						disabled: true,
						label: 'Laporan Kunjungan Site',
						children: (
							<CCAVisitReportVerificationSite
								onNext={() => {
									setActiveTabKey('approval');
								}}
							/>
						),
					},
					{
						key: 'call',
						disabled: true,
						label: 'Call Report',
						children: <CCAVisitReportVerificationCall />,
					},
					{
						key: 'approval',
						disabled: true,
						label: 'Approval',
						children: <CCAVisitReportApprovalForm />,
					},
				]}
			/>
		</Provider>
	);
};

export default CCAVisitReportVerificationPage;
